import { useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router";
import { Outlet, useLocation, useMatches } from "react-router-dom";
import { BiteLogo } from "../svgs";
import { applyPageMeta } from "../seo";
import { useDownloadInfo } from "../utils/storeLinks";
import { trackLandingVisit } from "../utils/landingAnalytics";

function Navigator() {
  const navigate = useNavigate();
  const download = useDownloadInfo();
  const handleHomeClick = () => {
    navigate("/");
  };
  return (
    <NavigationContainer>
      <LogoButton onClick={handleHomeClick} aria-label="Bite home">
        <BiteLogo width="60px" fill="black" />
      </LogoButton>
      <NavLinks aria-label="Primary navigation">
        <a href="/#features">Features</a>
        <a href="/#community">Community</a>
        <a href="/#collections">Collections</a>
      </NavLinks>
      <NavActions>
        <LoginLink href="/app">Log in</LoginLink>
        <DownloadButton href={download.url} target="_blank" rel="noreferrer">
          Download
        </DownloadButton>
      </NavActions>
    </NavigationContainer>
  );
}

const NavigationContainer = styled.div`
  width: min(100% - 40px, 980px);
  min-height: 68px;
  margin: 16px auto 0;
  padding: 10px 18px 10px 24px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  position: sticky;
  top: 16px;
  z-index: 20;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.055),
    0 2px 8px rgba(0, 0, 0, 0.025);
  backdrop-filter: blur(14px);

  @media (max-width: 590px) {
    width: calc(100% - 24px);
    min-height: 58px;
    margin-top: 12px;
    padding: 8px 10px 8px 16px;
    top: 12px;

    svg {
      width: 54px;
    }
  }
`;

const LogoButton = styled.button`
  padding: 0;
  border: 0;
  background: transparent;
  display: flex;
  align-items: center;
  cursor: pointer;

  svg {
    display: block;
  }

  &:focus-visible {
    outline: 3px solid #8ab4ff;
    outline-offset: 6px;
    border-radius: 6px;
  }
`;

const NavLinks = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: clamp(20px, 4vw, 52px);

  a {
    color: #515865;
    font-size: 0.95rem;
    font-weight: 600;
    text-decoration: none;
    transition: color 180ms ease;
  }

  a:hover {
    color: #050505;
  }

  @media (max-width: 700px) {
    display: none;
  }
`;

const NavActions = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const LoginLink = styled.a`
  color: #050505;
  font-size: 0.9rem;
  font-weight: 650;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 3px solid #8ab4ff;
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

const DownloadButton = styled.a`
  min-height: 38px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 18px;
  border-radius: 999px;
  background: #050505;
  color: #ffffff;
  font-size: 0.9rem;
  font-weight: 650;
  text-decoration: none;

  &:focus-visible {
    outline: 3px solid #8ab4ff;
    outline-offset: 4px;
  }
`;

const NavigationWithOutlet = () => {
  const { pathname } = useLocation();
  const notFound = useMatches().some(match => match.handle?.notFound);

  useEffect(() => {
    applyPageMeta(pathname, { notFound });
  }, [pathname, notFound]);

  useEffect(() => {
    if (!notFound) trackLandingVisit(pathname);
  }, [pathname, notFound]);

  return (
    <div>
      <Navigator />
      <Outlet />
    </div>
  );
};

export default NavigationWithOutlet;
